import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import Skeleton from '../Skeleton';

const PageContainer = styled.div`
  ${tw`
    flex
    flex-col
    md:flex-row
    p-4
    md:p-8
    gap-4
    md:gap-8
    mx-auto
    w-full max-w-full min-w-0 box-border`}
  max-width: 1280px; // Explicitly set max-width for larger screens

  @media (min-width: 1024px) {
    padding: 2rem 4rem;
  }
`;

const LeftSection = styled.div`
  ${tw`
    w-full
    order-2
    md:w-3/5
    lg:w-2/3
    md:order-1
  `}
`;

const RightSection = styled.div`
  ${tw`
    w-full
    order-1
    md:w-2/5
    lg:w-1/3
    md:order-2
  `}
`;

const CheckoutTitleSkeleton = styled(Skeleton)`
  ${tw`mb-6 md:mb-8`}
`;

const ProtectionSection = styled.div`
  ${tw`
    border
    border-border-default
    rounded-lg
    p-4
    md:p-6
    mb-6
  `}

  @media (min-width: 1024px) {
    max-width: 90%;
  }
`;

const ProtectionTitleSkeleton = styled(Skeleton)`
  ${tw`mb-4`}
`;

const ProtectionDescriptionSkeleton = styled(Skeleton)`
  ${tw`mb-6`}
`;

const ProtectionOptionSkeleton = styled.div`
  ${tw`
    border-2
    border-border-default
    rounded-lg
    p-4
    md:p-5
    mb-4
    md:mb-6
  `}
`;

const CarImageSkeleton = styled(Skeleton)`
  ${tw`
    w-full
    h-52
    md:h-64
    rounded-lg
    mb-6
  `}
`;

const CarInfoSkeleton = styled.div`
  ${tw`mb-4 md:mb-6`}
`;

const CarTitleRow = styled.div`
  ${tw`flex justify-between items-center mb-2 md:mb-3`}
`;

const DateTimeRow = styled.div`
  ${tw`mb-4 md:mb-5`}
`;

const LocationInfoSkeleton = styled.div`
  ${tw`
    flex
    items-center
    gap-2
    mt-2
    mb-6
    md:mb-8
  `}
`;

const PriceBreakdownSkeleton = styled.div`
  ${tw`mb-6 md:mb-8`}

  @media (min-width: 768px) {
    max-width: 90%;
  }
`;

const PriceRowSkeleton = styled.div`
  ${tw`
    flex
    items-center
    justify-between
    mb-3
    py-1
    md:py-2
  `}
`;

const TotalPriceRow = styled.div`
  ${tw`
    flex
    items-center
    justify-between
    py-2
    md:py-3
    border-t
    border-border-default
    mt-2
    md:mt-4
  `}
`;

const BookButtonContainer = styled.div`
  ${tw`w-full rounded-lg`}
  background-color: #8B5CF6;

  @media (min-width: 768px) {
    max-width: 90%;
  }
`;

const BookButtonSkeleton = styled(Skeleton)`
  ${tw`
    w-full
    h-14
    rounded-lg
  `}
`;

const NoThanksLink = styled(Skeleton)`
  ${tw`
    w-56
    h-5
    mx-auto
    mt-4
    md:mt-6
  `}
`;

const CheckoutPageSkeleton: React.FC = () => {
  return (
    <PageContainer>
      <LeftSection>
        <CheckoutTitleSkeleton width="180px" height="32px" />

        <ProtectionSection>
          <ProtectionTitleSkeleton width="220px" height="24px" className="md:w-64" />
          <ProtectionDescriptionSkeleton width="100%" height="80px" />

          {/* Standard protection option - highlighted */}
          <ProtectionOptionSkeleton style={{ borderColor: '#9AE6B4', backgroundColor: '#F0FFF4' }}>
            <Skeleton width="140px" height="20px" className="mb-3 md:mb-4 md:w-48" />
            <Skeleton width="100%" height="20px" />
          </ProtectionOptionSkeleton>

          {/* Minimum protection option */}
          <ProtectionOptionSkeleton>
            <Skeleton width="140px" height="20px" className="mb-3 md:mb-4 md:w-48" />
            <div className="pl-6 md:pl-8">
              <Skeleton width="90%" height="16px" className="mb-2 md:mb-3" />
              <Skeleton width="80%" height="16px" />
            </div>
          </ProtectionOptionSkeleton>

          {/* No thanks option */}
          <NoThanksLink className="md:w-64" />
        </ProtectionSection>
      </LeftSection>

      <RightSection>
        <CarImageSkeleton />

        <CarInfoSkeleton>
          <CarTitleRow>
            <Skeleton width="60%" height="24px" className="md:w-2/3 md:h-6" />
            <Skeleton width="40px" height="20px" className="md:w-16" />
          </CarTitleRow>

          <DateTimeRow>
            <Skeleton width="90%" height="20px" className="md:w-5/6" />
          </DateTimeRow>
        </CarInfoSkeleton>

        <LocationInfoSkeleton>
          <Skeleton width="20px" height="20px" borderRadius="50%" />
          <Skeleton width="90%" height="18px" className="md:w-5/6" />
        </LocationInfoSkeleton>

        {/* Price breakdown */}
        <PriceBreakdownSkeleton>
          {/* Trip Fee */}
          <PriceRowSkeleton>
            <Skeleton width="120px" height="18px" className="md:w-40" />
            <Skeleton width="70px" height="18px" className="md:w-24" />
          </PriceRowSkeleton>

          {/* Car Price */}
          <PriceRowSkeleton>
            <Skeleton width="140px" height="18px" className="md:w-48" />
            <Skeleton width="70px" height="18px" className="md:w-24" />
          </PriceRowSkeleton>

          {/* Protection plan */}
          <PriceRowSkeleton>
            <Skeleton width="130px" height="18px" className="md:w-44" />
            <Skeleton width="70px" height="18px" className="md:w-24" />
          </PriceRowSkeleton>

          {/* Tax */}
          <PriceRowSkeleton>
            <Skeleton width="80px" height="18px" className="md:w-32" />
            <Skeleton width="70px" height="18px" className="md:w-24" />
          </PriceRowSkeleton>

          {/* Total row */}
          <TotalPriceRow>
            <Skeleton width="60px" height="20px" className="md:w-24 md:h-6" />
            <Skeleton width="80px" height="22px" className="md:w-28 md:h-7" />
          </TotalPriceRow>
        </PriceBreakdownSkeleton>

        <BookButtonContainer>
          <BookButtonSkeleton />
        </BookButtonContainer>
      </RightSection>
    </PageContainer>
  );
};

export default CheckoutPageSkeleton;
